import cx from "classnames";
import { memo, useCallback, useMemo, useState } from "react";

import { Modal } from "atoms/modal/Modal";

import { PracticeLocation } from "../../utils/locationTransform";

// Types
export interface CityGroup {
  city: string;
  locations: PracticeLocation[];
}

// Internal Components
interface CitySelectorProps {
  cities: string[];
  selectedCity: string;
  onCityChange: (city: string) => void;
}

function CitySelector({
  cities,
  selectedCity,
  onCityChange,
}: CitySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleCitySelect = useCallback(
    (city: string) => {
      onCityChange(city);
      setIsOpen(false);
    },
    [onCityChange]
  );

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Don't render if there's only one city
  if (cities.length <= 1) {
    return null;
  }

  return (
    <>
      {/* City Selector Button */}
      <button
        onClick={handleToggle}
        className={cx(
          "box-border flex w-full items-center justify-between",
          "h-12 gap-2.5 px-6 py-4",
          "rounded-3xl",
          "bg-white/40 shadow-[0px_1.272px_15.267px_0px_rgba(0,0,0,0.05)]",
          "font-['DM_Sans'] text-base font-normal leading-[22px] text-[#091f24]",
          "transition-colors duration-200 ease-in-out"
        )}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span>{selectedCity}</span>
        <div
          className={cx(
            "flex h-6 w-6 items-center justify-center",
            "transition-transform duration-200",
            isOpen ? "rotate-180" : "rotate-90"
          )}
        >
          <svg
            width="7"
            height="14"
            viewBox="0 0 7 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L6 7L1 13"
              stroke="#6B7070"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      {/* iOS-style Modal */}
      <Modal
        open={isOpen}
        onClose={handleClose}
        header={
          <div className="border-b border-gray-200 p-4 text-center">
            <h3 className="font-['DM_Sans'] text-lg font-medium text-[#1b1b1b]">
              Select City
            </h3>
          </div>
        }
        bodyClassName="p-0"
      >
        <div className="divide-y divide-gray-100 pb-6">
          {cities.map((city) => (
            <button
              key={city}
              onClick={() => handleCitySelect(city)}
              className={cx(
                "w-full px-6 py-4 text-left",
                "font-['DM_Sans'] text-base font-normal leading-[22px]",
                "transition-colors duration-200",
                "active:bg-gray-50",
                selectedCity === city
                  ? "bg-brand-tint font-medium text-brand-color"
                  : "text-nearly-black hover:bg-gray-50"
              )}
              role="option"
              aria-selected={selectedCity === city}
            >
              <div className="flex items-center justify-between">
                <span>{city}</span>
                {selectedCity === city && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-brand-color"
                  >
                    <path
                      d="M16.25 6.25L8.125 14.375L3.75 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}

interface LocationItemProps {
  location: PracticeLocation;
  selected: boolean;
  onClick: () => void;
}

const LocationItem = memo(
  ({ location, selected, onClick }: LocationItemProps) => {
    const handleClick = useCallback(() => {
      onClick();
    }, [onClick]);

    return (
      <button
        onClick={handleClick}
        className={cx(
          "box-border flex w-full items-center gap-2.5 p-4",
          "rounded-[15.267px] shadow-[0px_1.272px_15.267px_0px_rgba(0,0,0,0.05)]",
          "transition-colors duration-200 ease-in-out",
          "text-left",
          selected ? "bg-brand-tint" : "bg-white/40"
        )}
      >
        {/* Radio Button */}
        <div className="relative flex h-[30px] w-[30px] shrink-0 items-center justify-center">
          <div
            className={cx(
              "relative h-4 w-4 rounded-full border border-solid",
              "flex items-center justify-center",
              "transition-colors duration-200",
              selected
                ? "border-brand-color bg-white"
                : "border-dark-grey bg-white"
            )}
          >
            {selected && (
              <div className="h-[10px] w-[10px] rounded-full bg-brand-color" />
            )}
          </div>
        </div>

        {/* Location Details */}
        <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
          <div
            className={cx(
              "font-['DM_Sans'] text-base font-normal leading-[22px]",
              "w-full truncate whitespace-nowrap text-[#1b1b1b]"
            )}
          >
            {location.name}
          </div>
          <div
            className={cx(
              "font-['DM_Sans'] text-sm font-normal leading-[18px]",
              "w-full truncate whitespace-nowrap text-[#1b1b1b]"
            )}
          >
            {location.city}, {location.state}
          </div>
        </div>
      </button>
    );
  }
);

LocationItem.displayName = "LocationItem";

// Main Component
/**
 * LocationListPickerV3 - A modern location picker component with city filtering
 *
 * Features:
 * - City-based filtering with iOS-style modal selector
 * - Radio button selection with visual feedback
 * - Support for both single and multi-select modes
 * - Automatic city selector hiding when only one city exists
 * - Mobile-optimized touch interactions
 *
 * @param items - Array of city groups with locations already organized by city
 * @param onSelectionChange - Callback fired when selection changes, receives array of selected location IDs
 * @param multiSelect - Whether multiple locations can be selected (default: true)
 */
interface LocationListPickerV3Props {
  /** Array of city groups with locations already organized by city */
  items?: CityGroup[];
  /** Callback fired when selection changes, receives array of selected location IDs */
  onSelectionChange?: (selectedIds: string[]) => void;
  /** Whether multiple locations can be selected */
  multiSelect?: boolean;
  /** Initially selected location IDs */
  initialSelectedIds?: string[];
}

export function LocationListPickerV3({
  items = [],
  onSelectionChange,
  multiSelect = true,
  initialSelectedIds = [],
}: LocationListPickerV3Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelectedIds)
  );

  // Get available cities
  const cities = useMemo(() => items.map((group) => group.city), [items]);

  // Find the city that contains the initially selected location
  const initialCity = useMemo(() => {
    if (initialSelectedIds.length > 0) {
      const cityGroup = items.find((group) =>
        group.locations.some((loc) => loc.id === initialSelectedIds[0])
      );
      return cityGroup?.city || cities[0] || "";
    }
    return cities[0] || "";
  }, [initialSelectedIds, items, cities]);

  // State for selected city (default to city containing preselected location, or first city)
  const [selectedCity, setSelectedCity] = useState<string>(initialCity);

  // Update selected city if cities change and current selection is no longer valid
  useMemo(() => {
    if (cities.length > 0 && !cities.includes(selectedCity)) {
      setSelectedCity(cities[0]);
    }
  }, [cities, selectedCity]);

  // Get locations for currently selected city
  const currentCityLocations = useMemo(() => {
    const cityGroup = items.find((group) => group.city === selectedCity);
    return cityGroup?.locations || [];
  }, [items, selectedCity]);

  const handleLocationClick = useCallback(
    (locationId: string) => {
      const newSelectedIds = new Set(selectedIds);

      if (multiSelect) {
        if (newSelectedIds.has(locationId)) {
          newSelectedIds.delete(locationId);
        } else {
          newSelectedIds.add(locationId);
        }
      } else {
        newSelectedIds.clear();
        newSelectedIds.add(locationId);
      }

      setSelectedIds(newSelectedIds);
      onSelectionChange?.(Array.from(newSelectedIds));
    },
    [selectedIds, multiSelect, onSelectionChange]
  );

  const handleCityChange = useCallback(
    (city: string) => {
      setSelectedCity(city);

      // When changing cities, if we're in single select mode, clear selections
      // In multi-select mode, keep selections from other cities
      if (!multiSelect) {
        setSelectedIds(new Set());
        onSelectionChange?.([]);
      }
    },
    [multiSelect, onSelectionChange]
  );

  if (!items || items.length === 0) {
    return (
      <div className="flex w-full items-center justify-center p-8 text-center">
        <div className="text-sm text-gray-500">No locations available</div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {/* City Selector - only shown if there are multiple cities */}
      {cities.length > 1 && (
        <CitySelector
          cities={cities}
          selectedCity={selectedCity}
          onCityChange={handleCityChange}
        />
      )}

      {/* Locations List for Selected City */}
      <div className="flex w-full flex-col gap-6">
        {currentCityLocations.map((location) => (
          <LocationItem
            key={location.id}
            location={location}
            selected={selectedIds.has(location.id)}
            onClick={() => handleLocationClick(location.id)}
          />
        ))}
      </div>

      {/* Empty state for selected city */}
      {currentCityLocations.length === 0 && selectedCity && (
        <div className="flex w-full items-center justify-center p-8 text-center">
          <div className="text-sm text-gray-500">
            No locations available in {selectedCity}
          </div>
        </div>
      )}
    </div>
  );
}
